import React, { useState } from "react"

import { formatBookableEventDurationLabel } from "../../../../../@core/bookableEventType/bookableEventType.utils"
import { getBuilderServiceItemLocationDetail } from "../../../utils/builderServiceItemUtils"

const AddServicesModal = ({ isOpen, onClose, onSave, practice, selectedItemIds = [] }) => {
  const [filter, setFilter] = useState("all")
  const [localSelectedIds, setLocalSelectedIds] = useState([...selectedItemIds])
  const [hoveredItemId, setHoveredItemId] = useState(null)

  if (!isOpen) return null

  // Get all available items
  // Services includes both services and packages (packages have package: true)
  const allServices = (practice?.services || []).filter((s) => s.public === true)
  const services = allServices.filter((s) => !s.package)
  const packages = allServices.filter((s) => s.package === true)
  // Show only event types (one row per type), not individual scheduled instances.
  const eventTypes = (practice?.bookableEventTypes || []).filter((e) => !e.deletedAt && !e.deleted_at)

  // Create unified list with type
  const allItems = [
    ...services.map((s) => ({ ...s, itemType: "service", label: "SERVICE" })),
    ...packages.map((p) => ({ ...p, itemType: "package", label: "PACKAGE" })),
    ...eventTypes.map((e) => ({ ...e, itemType: "event", label: "EVENT" }))
  ]

  // Filter items based on selected filter
  const filteredItems = allItems.filter((item) => {
    if (filter === "all") return true
    if (filter === "services") return item.itemType === "service"
    if (filter === "packages") return item.itemType === "package"
    if (filter === "events") return item.itemType === "event"
    return true
  })

  // Check if filter should be shown
  const hasServices = services.length > 0
  const hasPackages = packages.length > 0
  const hasEvents = eventTypes.length > 0
  const showFilter = [hasServices, hasPackages, hasEvents].filter(Boolean).length > 1

  const isSelected = (item) => {
    const id = `${item.itemType}-${item.id || item.slug}`
    return localSelectedIds.includes(id)
  }

  const toggleSelection = (item) => {
    const id = `${item.itemType}-${item.id || item.slug}`
    if (isSelected(item)) {
      setLocalSelectedIds(localSelectedIds.filter((selectedId) => selectedId !== id))
    } else {
      setLocalSelectedIds([...localSelectedIds, id])
    }
  }

  const handleSelectAll = () => {
    const allIds = filteredItems.map((item) => `${item.itemType}-${item.id || item.slug}`)
    setLocalSelectedIds([...new Set([...localSelectedIds, ...allIds])])
  }

  const handleSave = () => {
    onSave(localSelectedIds)
    onClose()
  }

  const selectedCount = localSelectedIds.length
  const totalCount = allItems.length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-builder-border-light px-8 py-6">
          <h2 className="font-['Manrope:Bold',_sans-serif] text-[20px] font-bold leading-[24px] text-builder-primary">
            Add Services, Events & Packages
          </h2>
          <button
            onClick={onClose}
            className="flex size-8 items-center justify-center text-builder-secondary hover:text-builder-primary">
            <svg className="size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter and Select All */}
        <div className="flex items-center justify-between border-b border-builder-border-light px-8 py-4">
          {/* Filters */}
          {showFilter && (
            <div className="flex items-center gap-6">
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="filter"
                  value="all"
                  checked={filter === "all"}
                  onChange={(e) => setFilter(e.target.value)}
                  className="size-6 cursor-pointer accent-builder-secondary"
                />
                <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                  All
                </span>
              </label>

              {hasServices && (
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="filter"
                    value="services"
                    checked={filter === "services"}
                    onChange={(e) => setFilter(e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                    Services
                  </span>
                </label>
              )}

              {hasPackages && (
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="filter"
                    value="packages"
                    checked={filter === "packages"}
                    onChange={(e) => setFilter(e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                    Packages
                  </span>
                </label>
              )}

              {hasEvents && (
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="radio"
                    name="filter"
                    value="events"
                    checked={filter === "events"}
                    onChange={(e) => setFilter(e.target.value)}
                    className="size-6 cursor-pointer accent-builder-secondary"
                  />
                  <span className="font-['Manrope:Regular',_sans-serif] text-[16px] font-normal leading-[24px] text-builder-primary">
                    Events
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Select All Button */}
          <button
            onClick={handleSelectAll}
            className="font-['Manrope:Bold',_sans-serif] text-[14px] font-bold leading-[20px] text-builder-accent hover:text-builder-accent-hover">
            Select All
          </button>
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto px-8 py-4">
          <div className="flex flex-col gap-3">
            {filteredItems.map((item) => {
              const selected = isSelected(item)
              const itemId = item.id || item.slug
              const displayName = item.name || item.title
              // Format details based on item type
              let details = ""
              if (item.itemType === "service") {
                const price = item.amountCents
                  ? `$${(item.amountCents / 100).toFixed(0)}-$${
                      item.maxAmountCents ? (item.maxAmountCents / 100).toFixed(0) : (item.amountCents / 100).toFixed(0)
                    }`
                  : ""
                const duration = item.duration ? `${item.duration}/90/120 min` : ""
                const locations = getBuilderServiceItemLocationDetail(item)
                details = [price, duration, locations].filter(Boolean).join(" • ")
              } else if (item.itemType === "package") {
                const price = item.amountCents ? `$${(item.amountCents / 100).toFixed(0)}` : ""
                const duration = item.duration ? `${item.duration}min` : ""
                const sessions = item.sessions ? `${item.sessions} Sessions` : ""
                const location = getBuilderServiceItemLocationDetail(item)
                details = [price, duration, sessions, location].filter(Boolean).join(" • ")
              } else if (item.itemType === "event") {
                // Events in selector: price (if available) and duration with correct unit (hour/min/day)
                const amountCents = item.amountCents ?? item.amount_cents
                let price = ""
                if (amountCents != null && amountCents > 0) {
                  price = `$${(amountCents / 100).toFixed(0)}`
                } else if (amountCents === 0) {
                  price = "Free"
                } else if (item.price) {
                  price = `$${item.price}`
                }
                const duration = formatBookableEventDurationLabel(item)
                details = [price, duration].filter(Boolean).join(" • ")
              }

              const itemKey = `${item.itemType}-${itemId}`
              const isHovered = hoveredItemId === itemKey

              return (
                <div
                  key={itemKey}
                  className={`flex items-start justify-between rounded-lg border p-4 transition-all ${
                    selected
                      ? "border-builder-accent bg-[#f0fafb]"
                      : "border-builder-border-light bg-white hover:border-builder-border hover:bg-[#f8fbfd]"
                  }`}
                  onMouseEnter={() => setHoveredItemId(itemKey)}
                  onMouseLeave={() => setHoveredItemId(null)}>
                  <div className="flex-1">
                    <div className="mb-1 font-['Manrope:Regular',_sans-serif] text-[12px] uppercase leading-[16px] tracking-wide text-builder-secondary">
                      {item.label}
                    </div>
                    <div className="mb-1 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold leading-[24px] text-builder-primary">
                      {displayName}
                    </div>
                    {details && (
                      <div className="font-['Manrope:Regular',_sans-serif] text-[14px] leading-[20px] text-builder-secondary">
                        {details}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center">
                    {selected ? (
                      isHovered ? (
                        <button
                          onClick={() => toggleSelection(item)}
                          className="flex items-center gap-2 text-[#bd654f] transition-colors hover:text-[#a55a45]">
                          <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span className="font-['Manrope:Regular',_sans-serif] text-[14px]">Remove</span>
                        </button>
                      ) : (
                        <span className="font-['Manrope:Regular',_sans-serif] text-[14px] text-builder-accent">
                          ✓ Selected
                        </span>
                      )
                    ) : (
                      <button
                        onClick={() => toggleSelection(item)}
                        className="rounded bg-builder-accent px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[14px] font-bold text-white transition-colors hover:bg-builder-accent-hover">
                        Select
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-builder-border-light px-8 py-6">
          <span className="font-['Manrope:Regular',_sans-serif] text-[14px] text-builder-secondary">
            {selectedCount} of {totalCount} Selected
          </span>
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="rounded border border-builder-border bg-white px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-builder-primary transition-colors hover:bg-[#f8fbfd]">
              Close
            </button>
            <button
              onClick={handleSave}
              className="rounded bg-builder-accent px-4 py-2 font-['Manrope:Bold',_sans-serif] text-[16px] font-bold text-white transition-colors hover:bg-builder-accent-hover">
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddServicesModal
